import axiosInstance from './axiosConfig';
import type { MangaAspectRatio } from '../../constants/manga';

// API base URL for fixing image URLs in development
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8003/api';
const STORAGE_BASE_URL = API_BASE_URL.replace('/api', '');

/**
 * Fix image URL for local development
 */
const fixImageUrl = (url: string): string => {
  if (!url) return url;
  if (url.startsWith('http://localhost/storage/')) {
    return url.replace('http://localhost/storage/', `${STORAGE_BASE_URL}/storage/`);
  }
  return url;
};

export interface MangaDialogue {
  id: string;
  character_id: string;
  text: string;
  order: number;
}

export interface MangaCharacter {
  id: string;
  name: string;
  age?: string;
  appearance: string;
  role: 'protagonist' | 'supporting' | 'extra';
  character_image_url?: string;
  character_image_s3_key?: string;
  character_image_status?: 'pending' | 'generating' | 'completed' | 'failed';
  character_image_error?: string;
}

export interface MangaPage {
  page_number: number;
  description?: string;
  scene_description?: string;
  narration?: string;
  dialogues?: MangaDialogue[];
  additional_instructions?: string;
  generation_status: 'pending' | 'generating' | 'completed' | 'failed';
  generated_image_url?: string;
  generated_s3_key?: string;
  generated_image_id?: number | null;
  current_version?: number;
  image_versions?: MangaPageImageVersion[];
  error_message?: string;
  // Legacy fields for compatibility
  selected_image_url?: string;
  selected_s3_key?: string;
}

export interface MangaPageImageVersion {
  version: number;
  generated_image_url: string;
  generated_s3_key?: string | null;
  generated_image_id?: number | null;
  created_at?: string | null;
  archived_at?: string | null;
  source?: 'generated' | 'smart_edit' | string;
}

export interface MangaGenerationJob {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  total_pages: number;
  completed_pages: number;
  current_page: number;
  progress_percentage: number;
  error_message?: string;
  started_at?: string;
  completed_at?: string;
}

export interface MangaProject {
  id: string;
  product_info: string;
  /** 商品の構造化データ（バックエンドで保存） */
  product_data?: {
    id: number;
    name: string;
    extended_metadata?: Record<string, unknown>;
  };
  story_prompt: string;
  style: string;
  image_model?: 'nanobanana2' | 'gpt-image-2';
  total_pages: number;
  characters?: MangaCharacter[];
  pages: MangaPage[];
  status: 'draft' | 'in_progress' | 'generating' | 'generating_characters' | 'completed' | 'failed';
  generation_progress?: number;
  error_message?: string;
  /** 生成比率。表示枠（mangaAspectClass）の決定に使う */
  aspect_ratio?: MangaAspectRatio;
  color_mode?: 'color' | 'monochrome';
  /** Tasky 上で公開中かどうか */
  is_public?: boolean;
  /** 公開中のときだけ URL が入る（非公開は null） */
  public_url?: string | null;
  /** 最終ページ下に出す CTA ボタンの文言 */
  cta_text?: string | null;
  /** CTA ボタンのリンク先（LP）。未設定ならボタンを出さない */
  cta_url?: string | null;
  created_at: string;
  updated_at: string;
}

/** PUT /manga/projects/{id}/publication のレスポンス data */
export interface MangaPublicationData {
  is_public: boolean;
  public_url: string | null;
  public_token: string | null;
  published_at: string | null;
  cta_text: string | null;
  cta_url: string | null;
}

export interface UpdateMangaPublicationRequest {
  is_public?: boolean;
  cta_text?: string | null;
  cta_url?: string | null;
}

/** GET /public/manga/{token} が返す 1 ページ分 */
export interface PublicMangaPage {
  page_number: number;
  image_url: string;
}

/** GET /public/manga/{token} のレスポンス data */
export interface PublicMangaData {
  title: string;
  aspect_ratio?: string | null;
  pages: PublicMangaPage[];
  cta: { text: string; url: string } | null;
  published_at?: string | null;
}

/**
 * 公開ページが見つからない／非公開のときに getPublicManga が投げる型付きエラー。
 * 404 と通信エラーを画面側で区別するために使う（404 は「未公開」表示）。
 */
export class MangaNotPublicError extends Error {
  constructor(message = 'この漫画は公開されていません') {
    super(message);
    this.name = 'MangaNotPublicError';
    // TS の extends Error + ES5 ターゲット対策（instanceof を効かせる）
    Object.setPrototypeOf(this, MangaNotPublicError.prototype);
  }
}

// backend の MangaProject::ensurePublicToken() が bin2hex(random_bytes(16)) で発行する32桁の16進数。
// ページURL由来の値なので、この形式以外は API を呼ばずに「未公開」扱いにする。
const PUBLIC_MANGA_TOKEN_PATTERN = /^[0-9a-f]{32}$/i;

export interface CharacterImagesStatusResponse {
  success: boolean;
  data: {
    characters: Array<{
      id: string;
      name: string;
      character_image_url: string | null;
      character_image_status: 'pending' | 'generating' | 'completed' | 'failed';
      character_image_error: string | null;
    }>;
    all_completed: boolean;
    completed_count: number;
    total_count: number;
  };
}

export interface TemporaryMangaImage {
  temp_id: string;
  url: string;
  s3_key: string;
  file_size: number;
  page_number: number;
}

export interface MangaStyleType {
  id: number;
  name: string;
  description: string;
  story_prompt_template: string;
  visual_style: string;
  thumbnail_url: string | null;
  sort_order: number;
  is_active: boolean;
}

export interface CreateMangaProjectRequest {
  product_info: string;
  product_data?: {
    id: number;
    name: string;
    extended_metadata?: Record<string, unknown>;
  };
  manga_style_id?: number;
  story_prompt?: string;
  custom_story_prompt?: string;
  page_count: number;
  style: string;
  aspect_ratio?: MangaAspectRatio;
  color_mode?: 'color' | 'monochrome';
  // Picks the image generator. Matches /image-generation — default is
  // gpt-image-2 (OpenAI), nanobanana2 routes through Gemini.
  image_model?: 'nanobanana2' | 'gpt-image-2';
  reference_images?: Array<{
    url?: string;
    s3_key?: string;
    image_data?: string;
    category: string;
  }>;
}

export interface CreateMangaProjectResponse {
  success: boolean;
  data?: MangaProject;
  error?: string;
  message?: string;
}

export interface GeneratePageImagesResponse {
  success: boolean;
  data: {
    images: TemporaryMangaImage[];
    page_number: number;
  };
  error?: string;
  message?: string;
}

export interface SelectPageImageResponse {
  success: boolean;
  data: MangaProject;
  error?: string;
  message?: string;
}

class MangaGenerationService {
  /**
   * Get available manga styles
   */
  async getMangaStyles(): Promise<{ success: boolean; data: MangaStyleType[] }> {
    const response = await axiosInstance.get<{ success: boolean; data: MangaStyleType[] }>(
      '/manga/styles'
    );
    return response.data;
  }

  /**
   * Create a new manga project and generate story structure
   */
  async createMangaProject(request: CreateMangaProjectRequest): Promise<CreateMangaProjectResponse> {
    const response = await axiosInstance.post<CreateMangaProjectResponse>(
      '/manga/projects',
      request
    );

    if (response.data.success && response.data.data) {
      response.data.data = this.fixProjectUrls(response.data.data);
    }

    return response.data;
  }

  /**
   * Generate 3 images for a specific page
   */
  async generatePageImages(projectId: string, pageIndex: number): Promise<GeneratePageImagesResponse> {
    const response = await axiosInstance.post<GeneratePageImagesResponse>(
      `/manga/projects/${projectId}/pages/${pageIndex}/generate`
    );

    // Fix image URLs
    if (response.data.success && response.data.data?.images) {
      response.data.data.images = response.data.data.images.map(img => ({
        ...img,
        url: fixImageUrl(img.url),
      }));
    }

    return response.data;
  }

  /**
   * Select an image for a page
   */
  async selectPageImage(
    projectId: string,
    pageIndex: number,
    tempId: string,
    s3Key: string,
    s3Url: string
  ): Promise<SelectPageImageResponse> {
    const response = await axiosInstance.post<SelectPageImageResponse>(
      `/manga/projects/${projectId}/pages/${pageIndex}/select`,
      { temp_id: tempId, s3_key: s3Key, s3_url: s3Url }
    );

    if (response.data.success && response.data.data) {
      response.data.data = this.fixProjectUrls(response.data.data);
    }

    return response.data;
  }

  /**
   * Delete a temporary image
   */
  async deleteTemporaryImage(s3Key: string): Promise<{ success: boolean; message: string }> {
    const response = await axiosInstance.post<{ success: boolean; message: string }>(
      '/manga/temporary-images/delete',
      { s3_key: s3Key }
    );
    return response.data;
  }

  /**
   * Get manga project details
   */
  async getProject(projectId: string): Promise<{ success: boolean; data: MangaProject }> {
    const response = await axiosInstance.get<{ success: boolean; data: MangaProject }>(
      `/manga/projects/${projectId}`
    );

    // Fix image URLs
    if (response.data.success && response.data.data) {
      response.data.data.pages = response.data.data.pages.map(page => ({
        ...page,
        selected_image_url: page.selected_image_url ? fixImageUrl(page.selected_image_url) : undefined,
      }));
    }

    return response.data;
  }

  /**
   * Download completed manga as ZIP
   */
  async downloadMangaZip(projectId: string): Promise<void> {
    const response = await axiosInstance.get(`/manga/projects/${projectId}/download`, {
      responseType: 'blob',
    });

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `manga-${projectId}.zip`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  /**
   * Get user's manga project history
   */
  async getProjectHistory(page: number = 1): Promise<{
    success: boolean;
    data: {
      data: MangaProject[];
      current_page: number;
      last_page: number;
      per_page: number;
      total: number;
    };
  }> {
    const response = await axiosInstance.get(`/manga/projects?page=${page}`);

    if (response.data.success && response.data.data?.data) {
      response.data.data.data = response.data.data.data.map((project: MangaProject) => this.fixProjectUrls(project));
    }

    return response.data;
  }

  /**
   * Delete a manga project
   */
  async deleteProject(projectId: string): Promise<{ success: boolean; message: string }> {
    const response = await axiosInstance.delete<{ success: boolean; message: string }>(
      `/manga/projects/${projectId}`
    );
    return response.data;
  }

  /**
   * Update characters of a manga project
   */
  async updateCharacters(
    projectId: string,
    characters: MangaCharacter[]
  ): Promise<{ success: boolean; data: MangaProject }> {
    const response = await axiosInstance.put<{ success: boolean; data: MangaProject }>(
      `/manga/projects/${projectId}/characters`,
      { characters }
    );

    if (response.data.success && response.data.data) {
      response.data.data = this.fixProjectUrls(response.data.data);
    }

    return response.data;
  }

  /**
   * Update a specific page of a manga project
   */
  async updatePage(
    projectId: string,
    pageNumber: number,
    data: {
      scene_description?: string;
      narration?: string;
      dialogues?: MangaDialogue[];
      additional_instructions?: string;
      reference_images?: Array<{ url: string; category?: string }>;
    }
  ): Promise<{ success: boolean; data: MangaProject }> {
    const response = await axiosInstance.put<{ success: boolean; data: MangaProject }>(
      `/manga/projects/${projectId}/pages/${pageNumber}`,
      data
    );

    if (response.data.success && response.data.data) {
      response.data.data = this.fixProjectUrls(response.data.data);
    }

    return response.data;
  }

  /**
   * Save a smart-edited image back to a manga page.
   */
  async updatePageImage(
    projectId: string,
    pageNumber: number,
    data: {
      generated_image_url: string;
      generated_s3_key?: string;
      generated_image_id?: number;
    }
  ): Promise<{ success: boolean; data: MangaProject }> {
    const response = await axiosInstance.put<{ success: boolean; data: MangaProject }>(
      `/manga/projects/${projectId}/pages/${pageNumber}/image`,
      data
    );

    if (response.data.success && response.data.data) {
      response.data.data = this.fixProjectUrls(response.data.data);
    }

    return response.data;
  }

  /**
   * Start batch generation of all pages
   */
  async startGeneration(projectId: string): Promise<{
    success: boolean;
    data: {
      project: MangaProject;
      job: MangaGenerationJob;
    };
    error?: string;
  }> {
    const response = await axiosInstance.post<{
      success: boolean;
      data: {
        project: MangaProject;
        job: MangaGenerationJob;
      };
      error?: string;
    }>(`/manga/projects/${projectId}/generate`);

    if (response.data.success && response.data.data?.project) {
      response.data.data.project = this.fixProjectUrls(response.data.data.project);
    }

    return response.data;
  }

  /**
   * Get generation status
   */
  async getGenerationStatus(projectId: string): Promise<{
    success: boolean;
    data: {
      project: MangaProject;
      job: MangaGenerationJob | null;
    };
  }> {
    const response = await axiosInstance.get<{
      success: boolean;
      data: {
        project: MangaProject;
        job: MangaGenerationJob | null;
      };
    }>(`/manga/projects/${projectId}/status`);

    if (response.data.success && response.data.data?.project) {
      response.data.data.project = this.fixProjectUrls(response.data.data.project);
    }

    return response.data;
  }

  /**
   * Regenerate a specific page
   */
  async regeneratePage(
    projectId: string,
    pageNumber: number,
    additionalInstructions?: string
  ): Promise<{
    success: boolean;
    data: {
      project: MangaProject;
      job: MangaGenerationJob;
    };
    error?: string;
  }> {
    const response = await axiosInstance.post<{
      success: boolean;
      data: {
        project: MangaProject;
        job: MangaGenerationJob;
      };
      error?: string;
    }>(`/manga/projects/${projectId}/pages/${pageNumber}/regenerate`, {
      additional_instructions: additionalInstructions,
    });

    if (response.data.success && response.data.data?.project) {
      response.data.data.project = this.fixProjectUrls(response.data.data.project);
    }

    return response.data;
  }

  /**
   * Generate character images for a project
   */
  async generateCharacterImages(projectId: string): Promise<{
    success: boolean;
    data: {
      project: MangaProject;
      message: string;
    };
    error?: string;
  }> {
    const response = await axiosInstance.post<{
      success: boolean;
      data: {
        project: MangaProject;
        message: string;
      };
      error?: string;
    }>(`/manga/projects/${projectId}/characters/generate`);

    if (response.data.success && response.data.data?.project) {
      response.data.data.project = this.fixProjectUrls(response.data.data.project);
    }

    return response.data;
  }

  /**
   * Regenerate a specific character's image
   */
  async regenerateCharacterImage(
    projectId: string,
    characterId: string
  ): Promise<{
    success: boolean;
    data: {
      project: MangaProject;
      message: string;
    };
    error?: string;
  }> {
    const response = await axiosInstance.post<{
      success: boolean;
      data: {
        project: MangaProject;
        message: string;
      };
      error?: string;
    }>(`/manga/projects/${projectId}/characters/${characterId}/regenerate`);

    if (response.data.success && response.data.data?.project) {
      response.data.data.project = this.fixProjectUrls(response.data.data.project);
    }

    return response.data;
  }

  /**
   * Get character images status
   */
  async getCharacterImagesStatus(projectId: string): Promise<CharacterImagesStatusResponse> {
    const response = await axiosInstance.get<CharacterImagesStatusResponse>(
      `/manga/projects/${projectId}/characters/status`
    );
    return response.data;
  }

  /**
   * 公開設定（公開フラグ / CTA 文言 / CTA リンク先）を更新する。
   * status !== 'completed' で is_public=true にすると backend が 422 を返す。
   */
  async updatePublication(
    projectId: string,
    payload: UpdateMangaPublicationRequest
  ): Promise<{ success: boolean; data: MangaPublicationData; message?: string }> {
    const response = await axiosInstance.put<{
      success: boolean;
      data: MangaPublicationData;
      message?: string;
    }>(`/manga/projects/${projectId}/publication`, payload);

    return response.data;
  }

  /**
   * 公開ページ用の取得（認証不要）。
   * 非公開 / 未完成 / 存在しないトークンは backend が 404 を返すので
   * MangaNotPublicError に変換して画面側で「未公開」を出せるようにする。
   */
  async getPublicManga(token: string): Promise<PublicMangaData> {
    if (!PUBLIC_MANGA_TOKEN_PATTERN.test(token)) {
      throw new MangaNotPublicError();
    }

    try {
      const response = await axiosInstance.get<{ success: boolean; data: PublicMangaData }>(
        `/public/manga/${encodeURIComponent(token)}`
      );

      if (!response.data?.success || !response.data.data) {
        throw new MangaNotPublicError();
      }

      const data = response.data.data;

      return {
        ...data,
        pages: (data.pages ?? []).map(page => ({
          ...page,
          image_url: fixImageUrl(page.image_url),
        })),
      };
    } catch (error) {
      if (error instanceof MangaNotPublicError) {
        throw error;
      }

      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        throw new MangaNotPublicError();
      }

      throw error;
    }
  }

  /**
   * Fix image URLs in a project
   */
  private fixProjectUrls(project: MangaProject): MangaProject {
    return {
      ...project,
      characters: project.characters?.map(char => ({
        ...char,
        character_image_url: char.character_image_url ? fixImageUrl(char.character_image_url) : undefined,
      })),
      pages: project.pages.map(page => ({
        ...page,
        generated_image_url: page.generated_image_url ? fixImageUrl(page.generated_image_url) : undefined,
        selected_image_url: page.selected_image_url ? fixImageUrl(page.selected_image_url) : undefined,
      })),
    };
  }
}

export default new MangaGenerationService();
